import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Gift, Crown, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import frameBg from '../assets/images/frame-bg.png';
import CreateRewardModal from '../components/gamification/rewards/CreateRewardModal';
import Toast from '../components/ui/Toast';
import { openCreateRewardModal, selectRewards, selectShowToast, selectToastMessage, hideToast } from '../store/slices/gamificationSlice';

const FeatureCard = ({ icon: Icon, title, description }) => {
  return (
    <Card className="hover:shadow-lg transition-shadow duration-300 border-gray-200 bg-white">
      <CardHeader className="items-center text-center">
        <div className="w-20 h-20 bg-purple-50 border-4 border-purple-200 rounded-3xl flex items-center justify-center mb-4">
          <Icon className="w-9 h-9 text-primary" />
        </div>
        <CardTitle className="text-xl">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-center text-gray-600">
          {description}
        </CardDescription>
      </CardContent>
    </Card>
  );
};

const Gamification = () => {
  const dispatch = useDispatch();
  const rewards = useSelector(selectRewards);
  const showToastState = useSelector(selectShowToast);
  const toastMessage = useSelector(selectToastMessage);
  const [activeTab, setActiveTab] = useState('gamification');

  const features = [
    {
      icon: Gift,
      title: 'Reward Your Ambassadors',
      description: 'Boost campaign performance by setting up rewards for ambassadors',
    },
    {
      icon: Crown,
      title: 'Set Milestones',
      description: 'Set up custom goals for sales, posts, or time-based achievements',
    },
    {
      icon: Percent,
      title: 'Customise Incentives',
      description: 'Create custom incentives like flat fees, free products, or special commissions.',
    },
  ];

  const handleEnableGamification = () => {
    dispatch(openCreateRewardModal());
  };

  const getEventDisplay = (reward) => {
    switch (reward.eventType) {
      case 'CROSS_SALES':
        return `Cross $${reward.eventConfig.salesAmount} in sales`;
      case 'POST_FREQUENCY':
        return `Posts ${reward.eventConfig.postCount} times every ${reward.eventConfig.duration}`;
      case 'IS_ONBOARDED':
        return 'Is Onboarded';
      default:
        return '';
    }
  };

  const getRewardDisplay = (reward) => {
    switch (reward.rewardType) {
      case 'FLAT_BONUS':
        return `Flat $${reward.rewardConfig.bonusAmount} Bonus`;
      case 'COMMISSION_TIER':
        return `Upgrade to ${reward.rewardConfig.tierName}`;
      default:
        return '';
    }
  };

  const getTimeBoundDisplay = (reward) => {
    if (!reward.isTimeBound || !reward.endDate) return '';
    const date = new Date(reward.endDate);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return ` (Ends: ${date.getDate()} ${months[date.getMonth()]}, ${date.getFullYear()})`;
  };

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'preferences', label: 'Preferences' },
    { id: 'gamification', label: 'Gamification' },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Tabs Navigation */}
      <div className="flex gap-8 border-b-2 border-gray-200 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              pb-4 px-2 font-medium transition-colors relative
              ${activeTab === tab.id
                ? 'text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
              }
            `}
          >
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Created Rewards</h2>
            <Button
              onClick={handleEnableGamification}
              className="bg-primary hover:bg-primary/90"
            >
              Create New Reward
            </Button>
          </div>

          {rewards.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-500 mb-4">No rewards created yet</p>
              <Button
                onClick={handleEnableGamification}
                variant="outline"
              >
                Create Your First Reward
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {rewards.map((reward) => (
                <Card key={reward.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-lg mb-2">
                          {getEventDisplay(reward)}
                        </h3>
                        <p className="text-purple-600 font-medium">
                          → {getRewardDisplay(reward)}
                          <span className="text-gray-500 text-sm">
                            {getTimeBoundDisplay(reward)}
                          </span>
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Created: {new Date(reward.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'preferences' && (
        <div className="text-center py-12">
          <p className="text-gray-500">Preferences content coming soon...</p>
        </div>
      )}

      {activeTab === 'gamification' && (
        <div className="relative min-h-[600px]">
          {/* Background Frame */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full flex justify-center pointer-events-none">
            <img
              src={frameBg}
              alt="frame"
              className="w-full max-w-3xl"
            />
          </div>

          {/* Hero Section */}
          <div className="text-center mb-12 relative z-10 pt-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              Gamify your Campaign
            </h1>
            <p className="text-gray-600 text-lg mb-8">
              Enable gamification to start crafting<br />
              your custom reward system.
            </p>
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base rounded-xl shadow-lg"
              onClick={handleEnableGamification}
            >
              Enable Gamification
            </Button>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 relative z-10">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      <CreateRewardModal />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        show={showToastState}
        onClose={() => dispatch(hideToast())}
      />
    </div>
  );
};

export default Gamification;
